/**
 * <nia-product-profile>
 * Anima las barras del perfil al entrar en pantalla y actualiza las filas de
 * detalle que dependen de la variante elegida.
 *
 * El ancho final lo calcula Liquid y viaja en la variable --nia-pp-target, que
 * el CSS ya usa como valor por defecto: sin JavaScript las barras se ven
 * correctas. Lo que hace este componente es llevarlas a cero y devolverlas a
 * su ancho al entrar en pantalla, para que la animación exista sin que el dato
 * dependa del script.
 */
class NiaProductProfile extends HTMLElement {
  connectedCallback() {
    this.fills = Array.from(this.querySelectorAll('[data-pp-fill]'));
    this.dynamicRows = Array.from(this.querySelectorAll('[data-pp-dynamic]'));

    document.addEventListener('nia:buy-options:change', this.#onVariantChange);

    // Con movimiento reducido o sin observer, se dejan como están: el CSS ya
    // les dio su ancho real.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    this.fills.forEach((fill) => {
      fill.style.width = '0%';
    });

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#fill(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.35 }
    );

    this.fills.forEach((fill) => this.observer.observe(fill));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('nia:buy-options:change', this.#onVariantChange);
  }

  #fill(element) {
    element.style.width = getComputedStyle(element).getPropertyValue('--nia-pp-target').trim() || '0%';
  }

  /**
   * El mapa viaja como "id:valor|id:valor" en lugar de JSON: es un puñado de
   * pares y así el atributo no necesita escaparse en Liquid.
   */
  #onVariantChange = (event) => {
    const variantId = event.detail?.variantId;
    if (!variantId) return;

    for (const row of this.dynamicRows) {
      const pair = row.dataset.ppDynamic
        .split('|')
        .map((entry) => entry.split(':'))
        .find(([id]) => id === String(variantId));

      const target = row.querySelector('[data-pp-value]');
      if (pair && target) target.textContent = pair.slice(1).join(':');
    }
  };
}

if (!customElements.get('nia-product-profile')) {
  customElements.define('nia-product-profile', NiaProductProfile);
}
